import { mutateMenuList } from "@/api/menuList/useMemuListQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { MasterApiMenuListsMenuListIdPutRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
});

export const MenuListUpdateForm = (params: any) => {
  const { formatterProps } = params;
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: updateMutate } = useMutation(
    (params: MasterApiMenuListsMenuListIdPutRequest) => mutateMenuList.update(params).mutationFn(),
    {
      onSuccess: () => {
        closeModal({});
        customAlert(
          t("message.editSuccess", { item: t("menuList.menuList") }),
          t("common.editSuccess"),
          "green"
        );
        queryClient.invalidateQueries(["menuLists"]);
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("menuList.code") }),
          t("common.editFail", { item: t("menuList.menuList") }),
          "red"
        );
      },
    }
  );

  const form = useForm({
    initialValues: {
      name: formatterProps.name,
      code: formatterProps.code,
      parentCode: formatterProps.parentCode,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        menuListId: formatterProps.id,
        menuListsGetRequest: {
          parentCode: form.values.parentCode!,
          name: form.values.name!,
          code: form.values.code!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("menuList.code") }),
        t("common.editFail", { item: t("menuList.menuList") }),
        "red"
      );
    }
  };

  return (
    <Update.Wrapper>
      <Update.Input
        label={t("menuList.parentCode")}
        withAsterisk
        disabled
        {...form.getInputProps("parentCode")}
      />
      <Update.Input
        disabled
        label={t("menuList.code")}
        withAsterisk
        {...form.getInputProps("code")}
      />
      <Update.Input label={t("menuList.name")} withAsterisk {...form.getInputProps("name")} />
      <Update.ButtonBox>
        <Update.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Update.Button>
        <Update.Button
          onClick={onSubmit}
          disabled={validateFormValues({
            pageType: "menuList",
            values: form.values,
          })}
        >
          {t("common.create", { item: t("menuList.menuList") })}
        </Update.Button>
      </Update.ButtonBox>
    </Update.Wrapper>
  );
};
